import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { Card } from '../components/ui/Card'; 
import { Database, Users, Calendar, List, Layers, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { initializeZoneDMApi } from '../services/zonedm';
import { GHL_AUTH_MISSING_MESSAGE, resolveGhlAuth, toZoneDmCredentials } from '../services/ghlAuth';

async function updateProviderPcMapping(providerId: string, pcStaffId: string | null) {
  const { error } = await supabase
    .from('providers')
    .update({ pc_staff_id: pcStaffId })
    .eq('id', providerId);
  if (error) throw error;
}

export default function DataExplorer() {
  const NOT_MAPPED_CALENDAR_ID = '__NOT_MAPPED__';
  const queryClient = useQueryClient();
  type SectionKey = 'providers' | 'appointmentSubtypes' | 'staff' | 'appointmentTypes' | 'hlCalendars' | 'hlOpportunityCustomFields' | 'procedures';
  const [collapsed, setCollapsed] = React.useState<Record<SectionKey, boolean>>({
    providers: false,
    appointmentSubtypes: false,
    staff: false,
    appointmentTypes: false,
    hlCalendars: false,
    hlOpportunityCustomFields: false,
    procedures: false,
  });
  const toggleSection = (key: SectionKey) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  const getLatestTimestamp = (rows: Array<Record<string, any>> | null | undefined, candidateFields: string[] = ['last_sync', 'updated_at', 'created_at']): string => {
    if (!rows || rows.length === 0) return '-';
    let latestMs = 0;
    for (const row of rows) {
      for (const field of candidateFields) {
        const value = row?.[field];
        if (value) {
          const ms = new Date(value).getTime();
          if (Number.isFinite(ms) && ms > latestMs) latestMs = ms;
        }
      }
    }
    return latestMs ? new Date(latestMs).toLocaleString() : '-';
  };
  const { data: providers } = useQuery({
    queryKey: ['providers'],
    queryFn: async () => {
      const { data } = await supabase
        .from('providers')
        .select('*')
        .order('created_at', { ascending: false });
      return data;
    }
  });

  const { data: staff } = useQuery({
    queryKey: ['staff'],
    queryFn: async () => {
      const { data } = await supabase
        .from('staff')
        .select('*')
        .order('created_at', { ascending: false });
      return data;
    }
  });

  const { data: appointmentTypes } = useQuery({
    queryKey: ['appointment_types'],
    queryFn: async () => {
      const { data } = await supabase
        .from('appointment_types')
        .select('*')
        .order('created_at', { ascending: false });
      return data;
    }
  });

  const { data: hlCalendars } = useQuery({
    queryKey: ['hl_calendars'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('hl_calendars')
        .select('*')
        .eq('user_id', session.user.id)
        .order('name', { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });

  const refreshCalendars = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const { data: zonedm, error } = await supabase
        .from('integrations')
        .select('user_id, access_token, location_id, config')
        .eq('type', 'zonedm')
        .eq('user_id', session.user.id)
        .maybeSingle();
      if (error) throw error;
      const auth = resolveGhlAuth(zonedm);
      if (!auth) {
        toast.error(GHL_AUTH_MISSING_MESSAGE);
        return;
      }
      const api = initializeZoneDMApi(toZoneDmCredentials(auth));
      const calendars = await api.listCalendars();
      const list = Array.isArray((calendars as any)?.data)
        ? (calendars as any).data
        : (Array.isArray((calendars as any)?.calendars)
          ? (calendars as any).calendars
          : (Array.isArray(calendars) ? (calendars as any) : []));
      if (!Array.isArray(list) || list.length === 0) {
        toast('No calendars returned');
        return;
      }
      const rows = list.map((c: any) => ({
        user_id: session.user.id,
        hl_calendar_id: c?.id || c?.calendarId || c?.uid,
        name: c?.name || c?.title || '',
        group_name: c?.groupName || c?.group || '',
      })).filter((r: any) => r.user_id && r.hl_calendar_id);
      if (rows.length) {
        await supabase.from('hl_calendars').upsert(rows, { onConflict: 'user_id,hl_calendar_id' });
        await queryClient.invalidateQueries({ queryKey: ['hl_calendars'] });
        toast.success(`Calendars refreshed (${rows.length})`);
      }
    } catch (e: any) {
      toast.error(e?.message || 'Failed to refresh calendars');
    }
  };

  // Opportunity Custom Fields - for mapping 4D data to GHL opportunity fields
  const { data: hlOpportunityCustomFields } = useQuery({
    queryKey: ['hl_opportunity_custom_fields'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('hl_opportunity_custom_fields')
        .select('*')
        .eq('user_id', session.user.id)
        .order('field_name', { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });

  const refreshOpportunityCustomFields = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const { data: zonedm, error } = await supabase
        .from('integrations')
        .select('user_id, access_token, location_id, config')
        .eq('type', 'zonedm')
        .eq('user_id', session.user.id)
        .maybeSingle();
      if (error) throw error;
      const auth = resolveGhlAuth(zonedm);
      if (!auth) {
        toast.error(GHL_AUTH_MISSING_MESSAGE);
        return;
      }

      const api = initializeZoneDMApi(toZoneDmCredentials(auth));
      const data = await api.listOpportunityCustomFields();
      const fields = data.customFields || [];
      const locationId = auth.locationId;
      
      if (!Array.isArray(fields) || fields.length === 0) {
        toast('No opportunity custom fields found in GHL');
        return;
      }
      
      // Build rows to upsert
      const rows = fields.map((f: any) => ({
        user_id: session.user.id,
        location_id: locationId,
        field_id: f.id,
        field_key: f.fieldKey || null,
        field_name: f.name || '',
        field_type: f.dataType || null,
        updated_at: new Date().toISOString(),
      })).filter((r: any) => r.field_id);
      
      if (rows.length) {
        const { error: upsertError } = await supabase
          .from('hl_opportunity_custom_fields')
          .upsert(rows, { onConflict: 'user_id,location_id,field_id' });
        
        if (upsertError) {
          toast.error(`Failed to save fields: ${upsertError.message}`);
          return;
        }
        
        await queryClient.invalidateQueries({ queryKey: ['hl_opportunity_custom_fields'] });
        toast.success(`Opportunity fields synced (${rows.length})`);
      }
    } catch (e: any) {
      toast.error(e?.message || 'Failed to refresh opportunity custom fields');
    }
  };

  const { data: typeCalendarMappings } = useQuery({
    queryKey: ['appointment_calendar_mappings'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('appointment_calendar_mappings')
        .select('*')
        .eq('user_id', session.user.id);
      if (error) throw error;
      return data || [];
    },
  });

  // Local editable mapping state keyed by appointment_type_external_id
  const [editableMappings, setEditableMappings] = React.useState<Record<string, string>>({});
  const [isSavingMappings, setIsSavingMappings] = React.useState(false);
  React.useEffect(() => {
    const initial: Record<string, string> = {};
    (typeCalendarMappings || []).forEach((m: any) => {
      if (
        m.appointment_type_external_id != null &&
        m.hl_calendar_id &&
        m.hl_calendar_id !== NOT_MAPPED_CALENDAR_ID
      ) {
        initial[String(m.appointment_type_external_id)] = m.hl_calendar_id;
      }
    });
    setEditableMappings(initial);
  }, [typeCalendarMappings]);

  const onChangeTypeCalendar = (externalId: number, hlCalendarId: string) => {
    setEditableMappings((prev) => ({ ...prev, [String(externalId)]: hlCalendarId }));
  };

  const onSaveMappings = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast.error('Not authenticated');
      return;
    }
    
    setIsSavingMappings(true);
    
    try {
      // Build rows to upsert (use sentinel for Not Mapped)
      const rowsToUpsert: Array<{ user_id: string; appointment_type_external_id: number; hl_calendar_id: string }> = [];
      
      Object.entries(editableMappings).forEach(([extId, calId]) => {
        const externalId = Number(extId);
        const value = (calId || '').trim();
        rowsToUpsert.push({
          user_id: session.user.id,
          appointment_type_external_id: externalId,
          hl_calendar_id: value === '' ? NOT_MAPPED_CALENDAR_ID : value,
        });
      });
      
      // Upsert all rows (including Not Mapped sentinel) in one go
      if (rowsToUpsert.length > 0) {
        const { error: upsertError } = await supabase
          .from('appointment_calendar_mappings')
          .upsert(rowsToUpsert, { onConflict: 'user_id,appointment_type_external_id' });
        
        if (upsertError) {
          throw new Error(`Failed to save mappings: ${upsertError.message}`);
        }
      }
      
      // Invalidate queries to refresh the UI
      await queryClient.invalidateQueries({ queryKey: ['appointment_calendar_mappings'] });
      
      const savedCount = rowsToUpsert.length;
      
      if (savedCount > 0) {
        toast.success(`Saved ${savedCount} mapping(s)`);
      }
      // If no changes, silently return (no toast needed)
    } catch (error: unknown) {
      console.error('Save error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save mappings';
      toast.error(errorMessage);
    } finally {
      setIsSavingMappings(false);
    }
  };

  const { data: staffServices } = useQuery({
    queryKey: ['staff_services'],
    queryFn: async () => {
      const { data } = await supabase
        .from('staff_services')
        .select('*')
        .order('created_at', { ascending: false });
      return data;
    }
  });

  const { data: appointmentSubtypes } = useQuery({
    queryKey: ['appointment_subtypes'],
    queryFn: async () => {
      const { data } = await supabase
        .from('appointment_subtypes')
        .select(`
          *,
          appointment_type:appointment_types(
            name
          )
        `)
        .order('created_at', { ascending: false });
      return data;
    }
  });

  const { data: procedures } = useQuery({
    queryKey: ['procedures'],
    queryFn: async () => {
      const { data } = await supabase
        .from('procedures')
        .select('*')
        .order('created_at', { ascending: false });
      return data;
    }
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Data Explorer</h1>
          <p className="mt-1 text-sm text-gray-500">
            View and manage your synchronized EMR data
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Providers</p>
              <p className="text-2xl font-semibold mt-1">{providers?.length || 0}</p>
            </div>
            <div className="p-3 bg-indigo-50 rounded-lg">
              <Users className="w-6 h-6 text-indigo-600" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Staff</p>
              <p className="text-2xl font-semibold mt-1">{staff?.length || 0}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Types & Subtypes</p>
              <p className="text-2xl font-semibold mt-1">{appointmentTypes?.length || 0}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <Calendar className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            {appointmentSubtypes?.length || 0} subtypes
          </p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Staff Services</p>
              <p className="text-2xl font-semibold mt-1">{staffServices?.length || 0}</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <List className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Procedures</p>
              <p className="text-2xl font-semibold mt-1">{procedures?.length || 0}</p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg">
              <Database className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Data Tables */}
      <div className="space-y-6">
        {/* Providers */}
        <Card>
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <button onClick={() => toggleSection('providers')} className="flex items-center gap-2 group">
              {collapsed.providers ? (
                <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
              )}
              <h3 className="text-lg font-medium text-gray-900">Providers</h3>
            </button>
            <div className="text-sm text-gray-500 flex items-center gap-4">
              <span>Total: {providers?.length || 0}</span>
              <span>Last Sync: {getLatestTimestamp(providers, ['last_sync', 'updated_at'])}</span>
            </div>
          </div>
          {!collapsed.providers && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Degree</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Practice Consultant (PC)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Sync</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {providers?.map((provider: any) => (
                  <tr key={provider.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {provider.first_name} {provider.last_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{provider.degree}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:ring-indigo-500 focus:border-indigo-500"
                        value={provider.pc_staff_id || ''}
                        onChange={async (e) => {
                          const newPcId = e.target.value || null;
                          try {
                            await updateProviderPcMapping(provider.id, newPcId);
                            await queryClient.invalidateQueries({ queryKey: ['providers'] });
                            toast.success(`PC mapping updated for ${provider.first_name} ${provider.last_name}`);
                          } catch (err: any) {
                            toast.error(`Failed to update PC: ${err?.message || String(err)}`);
                          }
                        }}
                      >
                        <option value="">Self (default)</option>
                        {staff
                          ?.filter((s: any) => s.is_active)
                          .map((s: any) => (
                            <option key={s.id} value={s.id}>
                              {s.first_name} {s.last_name}{s.display_name && s.display_name !== `${s.first_name} ${s.last_name}` ? ` (${s.display_name})` : ''}
                            </option>
                          ))}
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        provider.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {provider.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(provider.last_sync).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </Card>

        {/* Appointment Types (moved above Subtypes) */}
        <Card>
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <button onClick={() => toggleSection('appointmentTypes')} className="flex items-center gap-2 group">
              {collapsed.appointmentTypes ? (
                <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
              )}
              <h3 className="text-lg font-medium text-gray-900">Appointment Types</h3>
            </button>
            <div className="text-sm text-gray-500 flex items-center gap-4">
              <span>Total: {appointmentTypes?.length || 0}</span>
              <span>Last Sync: {getLatestTimestamp(appointmentTypes, ['last_sync', 'updated_at'])}</span>
            </div>
          </div>
          {!collapsed.appointmentTypes && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">HL Calendar</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Sync</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {appointmentTypes?.map((type: any) => (
                  <tr key={type.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{type.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{type.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        className="border border-gray-300 rounded-md text-sm px-2 py-1 max-w-[260px]"
                        value={editableMappings[String(type.external_id)] || ''}
                        onChange={(e) => onChangeTypeCalendar(type.external_id, e.target.value)}
                      >
                        <option value="">Not Mapped</option>
                        {(hlCalendars || []).map((cal: any) => (
                          <option key={cal.hl_calendar_id} value={cal.hl_calendar_id}>
                            {(cal.group_name ? `${cal.group_name} · ` : '') + (cal.name || cal.hl_calendar_id)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        type.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {type.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(type.last_sync).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
          {!collapsed.appointmentTypes && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end">
              <button
                onClick={onSaveMappings}
                disabled={isSavingMappings}
                className="bg-indigo-600 text-white px-4 py-2 rounded shadow-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSavingMappings ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Mappings'
                )}
              </button>
            </div>
          )}
        </Card>

        {/* Appointment Subtypes */}
        <Card>
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <button onClick={() => toggleSection('appointmentSubtypes')} className="flex items-center gap-2 group">
              {collapsed.appointmentSubtypes ? (
                <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
              )}
              <h3 className="text-lg font-medium text-gray-900">Appointment Subtypes</h3>
            </button>
            <div className="text-sm text-gray-500 flex items-center gap-4">
              <span>Total: {appointmentSubtypes?.length || 0}</span>
              <span>Last Sync: {getLatestTimestamp(appointmentSubtypes)}</span>
            </div>
          </div>
          {!collapsed.appointmentSubtypes && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parent Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Sync</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {appointmentSubtypes?.map((subtype: any) => (
                  <tr key={subtype.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Layers className="w-4 h-4 text-gray-400 mr-2" />
                        <div className="text-sm font-medium text-gray-900">{subtype.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{subtype.appointment_type?.name || 'Unknown'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(subtype.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </Card>

        {/* Staff */}
        <Card>
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <button onClick={() => toggleSection('staff')} className="flex items-center gap-2 group">
              {collapsed.staff ? (
                <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
              )}
              <h3 className="text-lg font-medium text-gray-900">Staff</h3>
            </button>
            <div className="text-sm text-gray-500 flex items-center gap-4">
              <span>Total: {staff?.length || 0}</span>
              <span>Last Sync: {getLatestTimestamp(staff, ['last_sync', 'updated_at'])}</span>
            </div>
          </div>
          {!collapsed.staff && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Display Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Sync</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {staff?.map((member: any) => (
                  <tr key={member.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {member.first_name} {member.last_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{member.display_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        member.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {member.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(member.last_sync).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </Card>

        {/* Duplicate Appointment Types block removed */}

        {/* ZoneDM Calendars */}
        <Card>
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <button onClick={() => toggleSection('hlCalendars')} className="flex items-center gap-2 group">
              {collapsed.hlCalendars ? (
                <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
              )}
              <h3 className="text-lg font-medium text-gray-900">ZoneDM Calendars</h3>
            </button>
            <div className="text-sm text-gray-500 flex items-center gap-4">
              <span>Total: {hlCalendars?.length || 0}</span>
              <button onClick={refreshCalendars} className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded">
                Refresh
              </button>
            </div>
          </div>
          {!collapsed.hlCalendars && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Group</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Calendar ID</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(hlCalendars || []).map((cal: any) => (
                  <tr key={cal.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{cal.name || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{cal.group_name || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{cal.hl_calendar_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </Card>

        {/* ZoneDM Opportunity Custom Fields */}
        <Card>
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <button onClick={() => toggleSection('hlOpportunityCustomFields')} className="flex items-center gap-2 group">
              {collapsed.hlOpportunityCustomFields ? (
                <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
              )}
              <h3 className="text-lg font-medium text-gray-900">ZoneDM Opportunity Custom Fields</h3>
            </button>
            <div className="text-sm text-gray-500 flex items-center gap-4">
              <span>Total: {hlOpportunityCustomFields?.length || 0}</span>
              {(!hlOpportunityCustomFields || hlOpportunityCustomFields.length === 0) && (
                <span className="text-orange-600 font-medium">⚠️ Not synced - Click Refresh</span>
              )}
              <button onClick={refreshOpportunityCustomFields} className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded">
                Refresh
              </button>
            </div>
          </div>
          {!collapsed.hlOpportunityCustomFields && (
          <div className="overflow-x-auto">
            {(!hlOpportunityCustomFields || hlOpportunityCustomFields.length === 0) ? (
              <div className="px-6 py-8 text-center">
                <p className="text-gray-500 mb-4">No opportunity custom fields synced yet.</p>
                <p className="text-sm text-gray-400 mb-4">
                  These fields are required for populating 4D data (Provider, Appointment Type, Consult Reason, etc.) on ZoneDM opportunities.
                </p>
                <button 
                  onClick={refreshOpportunityCustomFields} 
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                >
                  Sync Opportunity Fields from ZoneDM
                </button>
              </div>
            ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field Key</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field ID</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(hlOpportunityCustomFields || []).map((field: any) => (
                  <tr key={field.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{field.field_name || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{field.field_key || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{field.field_type || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 font-mono text-xs">{field.field_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </div>
          )}
        </Card>

        {/* Procedures */}
        <Card>
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <button onClick={() => toggleSection('procedures')} className="flex items-center gap-2 group">
              {collapsed.procedures ? (
                <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
              )}
              <h3 className="text-lg font-medium text-gray-900">Procedures</h3>
            </button>
            <div className="text-sm text-gray-500 flex items-center gap-4">
              <span>Total: {procedures?.length || 0}</span>
              <span>Last Sync: {getLatestTimestamp(procedures, ['last_sync', 'updated_at'])}</span>
            </div>
          </div>
          {!collapsed.procedures && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CPT</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sync Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {procedures?.map((proc: any) => (
                  <tr key={proc.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{proc.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{proc.cpt || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        proc.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {proc.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {proc.procedure_fee != null ? `$${Number(proc.procedure_fee).toFixed(2)}` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        proc.sync_status === 'zonedm_synced' ? 'bg-green-100 text-green-800'
                        : proc.sync_status === 'zonedm_error' ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                      }`}>
                        {proc.sync_status || 'pending'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </Card>
      </div>
    </div>
  );
}